import React, { useState, useEffect } from "react";
import { Phone, Globe, Menu, X } from "lucide-react";
import { SITE_CONFIG, telHref } from "../lib/config";
import { trackCall, trackEvent } from "../lib/analytics";

export default function Header({ lang, T, scrollTo, onChangeLang }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const nav = [
    { id: "how", label: T.nav.how[lang] },
    { id: "models", label: T.nav.models[lang] },
    { id: "segments", label: T.nav.forWhom[lang] },
    { id: "trust", label: T.nav.delivery[lang] },
    { id: "faq", label: T.nav.faq[lang] },
  ];

  const go = (id) => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all ${scrolled ? "py-2" : "py-3"}`}
    >
      <div className={`mx-auto max-w-7xl px-4 md:px-6 ${scrolled ? "" : ""}`}>
        <div className="glass rounded-2xl px-4 py-3 md:px-5 md:py-3 flex items-center justify-between">
          <button
            onClick={() => go("top")}
            className="flex items-center gap-2"
            data-testid="logo-button"
          >
            <span className="w-8 h-8 rounded-xl bg-gradient-to-br from-ice-400 to-ice-300 grid place-items-center shadow-glow">
              <span className="text-ink-900 font-black text-sm">H</span>
            </span>
            <div className="leading-tight text-left">
              <div className="font-display font-bold text-white text-[15px] tracking-tight">
                {SITE_CONFIG.brand}
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-ice-400">
                {SITE_CONFIG.brandTag[lang]}
              </div>
            </div>
          </button>

          <nav className="hidden lg:flex items-center gap-7 text-sm text-muted">
            {nav.map((n) => (
              <button
                key={n.id}
                onClick={() => go(n.id)}
                className="hover:text-white transition-colors"
                data-testid={`nav-${n.id}`}
              >
                {n.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-1 rounded-full bg-ink-700/60 border border-white/10 p-1">
              {["ru", "uz"].map((l) => (
                <button
                  key={l}
                  data-testid={`lang-switch-${l}`}
                  onClick={() => onChangeLang(l)}
                  className={`px-3 py-1 text-xs font-semibold rounded-full transition ${
                    lang === l ? "bg-ice-400 text-ink-900" : "text-muted hover:text-white"
                  }`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>

            <a
              href={telHref}
              onClick={() => trackCall("header")}
              data-testid="header-call-button"
              className="hidden md:inline-flex items-center gap-2 btn-call rounded-full px-4 py-2.5 text-sm shadow-flame"
            >
              <Phone size={16} strokeWidth={2.6} />
              <span>{T.cta.callShort[lang]}</span>
            </a>

            <a
              href={telHref}
              onClick={() => trackCall("header_mobile")}
              data-testid="header-call-icon"
              className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full btn-call"
              aria-label={T.cta.callShort[lang]}
            >
              <Phone size={18} strokeWidth={2.6} />
            </a>

            <button
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full bg-ink-700/60 border border-white/10 text-white"
              data-testid="mobile-menu-toggle"
              aria-label="menu"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden mt-2 glass rounded-2xl p-4 space-y-1" data-testid="mobile-menu">
            {nav.map((n) => (
              <button
                key={n.id}
                onClick={() => go(n.id)}
                className="block w-full text-left px-3 py-2 rounded-xl text-white hover:bg-white/5"
                data-testid={`mobile-nav-${n.id}`}
              >
                {n.label}
              </button>
            ))}
            <div className="flex items-center gap-2 pt-2">
              {["ru", "uz"].map((l) => (
                <button
                  key={l}
                  onClick={() => {
                    onChangeLang(l);
                    setOpen(false);
                  }}
                  data-testid={`mobile-lang-${l}`}
                  className={`flex-1 px-3 py-2 rounded-xl text-xs font-semibold ${
                    lang === l ? "bg-ice-400 text-ink-900" : "bg-white/5 text-muted"
                  }`}
                >
                  <Globe className="inline mr-2" size={14} />
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
